import React, { useMemo, useState } from "react";
import { List, message, Modal, Space, theme, Typography } from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  SendOutlined,
} from "@ant-design/icons";
import { useAppDispatch } from "../../../../app/hooks";
import { IReleaseNote } from "../../../../interfaces/releaseNote.interface";
import { publishReleaseNoteThunk } from "../../releaseNotesSlice";
import {
  AUDIENCE_LABELS,
  AUDIENCE_ORDER,
  audienceHasContent,
  audienceLanguages,
  LANGUAGE_LABELS,
} from "../../releaseNotesHelpers";

const { Text } = Typography;

interface PublishModalProps {
  open: boolean;
  releaseNote: IReleaseNote;
  onClose: () => void;
  onPublished: () => void;
}

const PublishModal: React.FC<PublishModalProps> = ({
  open,
  releaseNote,
  onClose,
  onPublished,
}) => {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const [submitting, setSubmitting] = useState(false);

  const readyAudiences = useMemo(
    () => AUDIENCE_ORDER.filter((a) => audienceHasContent(releaseNote, a)),
    [releaseNote]
  );

  const handlePublish = async () => {
    if (readyAudiences.length === 0) {
      message.warning("No audience has content to publish.");
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(
        publishReleaseNoteThunk({
          id: releaseNote._id,
          audiences: readyAudiences,
        })
      ).unwrap();
      message.success("Release note published.");
      onPublished();
    } catch (err) {
      message.error("Failed to publish release note.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Publish Release Note?"
      open={open}
      onCancel={onClose}
      okText="Publish Now"
      okButtonProps={{
        icon: <SendOutlined />,
        loading: submitting,
        disabled: readyAudiences.length === 0,
      }}
      onOk={handlePublish}
    >
      <Text type="secondary">
        This will publish the ready audiences immediately.
      </Text>
      <List
        style={{ marginTop: token.margin }}
        bordered
        dataSource={AUDIENCE_ORDER}
        renderItem={(audience) => {
          const ready = audienceHasContent(releaseNote, audience);
          const langs = audienceLanguages(releaseNote, audience)
            .map((l) => LANGUAGE_LABELS[l])
            .join(", ");
          return (
            <List.Item>
              <Space>
                {ready ? (
                  <CheckCircleTwoTone twoToneColor={token.colorSuccess} />
                ) : (
                  <CloseCircleTwoTone twoToneColor={token.colorError} />
                )}
                <Text strong>{AUDIENCE_LABELS[audience]}</Text>
                {ready && <Text type="secondary">({langs})</Text>}
              </Space>
              <Text type={ready ? "success" : "danger"}>
                {ready ? "Ready to publish" : "No content (Skipping)"}
              </Text>
            </List.Item>
          );
        }}
      />
    </Modal>
  );
};

export default PublishModal;
